/**
 * Game types — the recurring templates behind Normal Bingo and Arif Bingo (§4).
 *
 * Editing a row here never touches a round that's already `waiting` or
 * `live`: `entry_fee`/`pattern_id`/thresholds are copied onto a round the
 * moment it's created, so a change here only ever reaches the *next* round
 * the runner opens for this type. That's a property of how rounds are
 * created, not something enforced on this screen — but it's why saving here
 * doesn't ask "apply to the current round too?": there's no such thing.
 *
 * Guarded the same way `payment_destination.manage` is: reading the list only
 * needs `round.read` (an operator already sees round state), but saving needs
 * `game_type.manage`, superadmin-only — this is the entry fee and win pattern
 * for every future round of a type, the same "highest-leverage" reasoning.
 *
 * Cadence (the 1-minute interval, or the Sat/Sun 16:00 schedule) is shown
 * read-only: switching an interval type to weekly (or back) is a structural
 * change this screen leaves to the database. What *is* editable is the interval
 * length for an interval type, plus every knob the round-builder reads.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { santim, formatEtb } from '@bingo/shared';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { at, parseAmount } from '../format.ts';
import { navigate } from '../router.ts';
import { ConfirmButton, ErrorBanner, Field, Row, Spinner } from '../components/ui.tsx';
import type { AdminView, GameTypeView, RoundView, WinningPattern } from '../api/types.ts';

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function GameTypes({ me, slug }: { me: AdminView; slug?: string }) {
  const [gameTypes, setGameTypes] = useState<GameTypeView[] | null>(null);
  const [patterns, setPatterns] = useState<WinningPattern[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [{ gameTypes: types }, { patterns: list }] = await Promise.all([
        api.gameTypes(),
        api.patterns(),
      ]);
      setGameTypes(types);
      setPatterns(list);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const manage = can(me.role, 'game_type.manage');

  const active = gameTypes?.find((gt) => gt.slug === slug) ?? gameTypes?.[0] ?? null;

  return (
    <section>
      <header className="pagehead">
        <h1>Game types</h1>
      </header>

      <ErrorBanner error={error} />

      {gameTypes === null || patterns === null ? (
        <Spinner />
      ) : (
        <>
          <div className="tabs">
            {gameTypes.map((gt) => (
              <button
                key={gt.id}
                type="button"
                className={`tabs__tab${gt.id === active?.id ? ' tabs__tab--on' : ''}`}
                onClick={() => navigate({ name: 'gameTypes', slug: gt.slug })}
              >
                {gt.name}
              </button>
            ))}
          </div>

          {active ? (
            <GameTypeCard
              key={active.id}
              me={me}
              gameType={active}
              patterns={patterns}
              editable={manage}
              onSaved={(updated) =>
                setGameTypes((prev) =>
                  (prev ?? []).map((g) => (g.id === updated.id ? updated : g)),
                )
              }
              onError={setError}
            />
          ) : (
            <p className="muted">No game types configured.</p>
          )}
        </>
      )}
    </section>
  );
}

function GameTypeCard({
  me,
  gameType,
  patterns,
  editable,
  onSaved,
  onError,
}: {
  me: AdminView;
  gameType: GameTypeView;
  patterns: WinningPattern[];
  editable: boolean;
  onSaved: (updated: GameTypeView) => void;
  onError: (err: unknown) => void;
}) {
  const [entryFee, setEntryFee] = useState(formatEtb(santim(gameType.entryFee)));
  const [patternId, setPatternId] = useState(gameType.patternId);
  const [minPlayers, setMinPlayers] = useState(String(gameType.minPlayers));
  const [maxPlayers, setMaxPlayers] = useState(
    gameType.maxPlayers === null ? '' : String(gameType.maxPlayers),
  );
  const [maxCardsPerUser, setMaxCardsPerUser] = useState(String(gameType.maxCardsPerUser));
  const [intervalSeconds, setIntervalSeconds] = useState(
    gameType.intervalSeconds === null ? '' : String(gameType.intervalSeconds),
  );
  const [scheduleDays, setScheduleDays] = useState<number[]>(gameType.scheduleDays ?? []);
  const [concurrentRounds, setConcurrentRounds] = useState(String(gameType.concurrentRounds));
  const [drawIntervalMs, setDrawIntervalMs] = useState(
    gameType.drawIntervalMs === null ? '' : String(gameType.drawIntervalMs / 1000),
  );
  const [phantomMinPlayers, setPhantomMinPlayers] = useState(
    gameType.phantomMinPlayers === null ? '' : String(gameType.phantomMinPlayers),
  );
  const [active, setActive] = useState(gameType.active);
  const [cartelaPoolSize, setCartelaPoolSize] = useState(
    gameType.cartelaPoolSize === null ? '' : String(gameType.cartelaPoolSize),
  );
  const [fixedPrizeAmount, setFixedPrizeAmount] = useState(
    gameType.fixedPrizeAmount === null ? '' : formatEtb(santim(gameType.fixedPrizeAmount)),
  );
  const [commissionPercent, setCommissionPercent] = useState(
    gameType.commissionBps === null ? '' : String(gameType.commissionBps / 100),
  );
  // Weekly types (Arif) run on an hours-scale exit window (24h, now 10h);
  // interval types (Normal) run on a seconds-scale one (the universal
  // default is 15s) — hours would round a 15s value to 0. Same underlying
  // field, unit picked by cadence so each type gets a sane granularity.
  const exitWindowUnitSeconds = gameType.cadence === 'weekly' ? 3600 : 1;
  const [exitClosesBeforeSeconds, setExitClosesBeforeSeconds] = useState(
    gameType.exitClosesBeforeSeconds === null
      ? ''
      : String(gameType.exitClosesBeforeSeconds / exitWindowUnitSeconds),
  );
  const [joinClosesBeforeSeconds, setJoinClosesBeforeSeconds] = useState(
    gameType.joinClosesBeforeSeconds === null ? '' : String(gameType.joinClosesBeforeSeconds / 3600),
  );
  const [graceBalls, setGraceBalls] = useState(gameType.realPlayerGraceBalls);
  const [allowRealWins, setAllowRealWins] = useState(gameType.allowRealWinsOverPhantoms);
  const [dualWinnerPct, setDualWinnerPct] = useState(
    Math.round((gameType.dualWinnerChance ?? 0) * 100),
  );
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    const fee = parseAmount(entryFee);
    if (!fee.ok) {
      onError(new Error('Entry fee must look like 10 or 10.00'));
      return;
    }
    let fixedPrize: string | null = null;
    if (fixedPrizeAmount.trim() !== '') {
      const parsed = parseAmount(fixedPrizeAmount);
      if (!parsed.ok) {
        onError(new Error('Fixed prize must look like 50000 or 50000.00'));
        return;
      }
      fixedPrize = parsed.santim;
    }
    if (gameType.cadence === 'weekly' && scheduleDays.length === 0) {
      onError(new Error('Pick at least one day'));
      return;
    }
    if (
      gameType.cadence === 'interval' &&
      (concurrentRounds.trim() === '' || Number(concurrentRounds) < 1 || Number(concurrentRounds) > 10)
    ) {
      onError(new Error('Concurrent rounds must be between 1 and 10'));
      return;
    }
    setBusy(true);
    try {
      const updated = await api.updateGameType(gameType.id, {
        entryFee: fee.santim,
        patternId,
        minPlayers: Number(minPlayers),
        maxPlayers: maxPlayers.trim() === '' ? null : Number(maxPlayers),
        maxCardsPerUser: Number(maxCardsPerUser),
        ...(gameType.cadence === 'interval'
          ? { intervalSeconds: Number(intervalSeconds), concurrentRounds: Number(concurrentRounds) }
          : { scheduleDays: [...scheduleDays].sort((a, b) => a - b) }),
        drawIntervalMs: Number(drawIntervalMs) * 1000,
        phantomMinPlayers: phantomMinPlayers.trim() === '' ? null : Number(phantomMinPlayers),
        active,
        cartelaPoolSize: cartelaPoolSize.trim() === '' ? null : Number(cartelaPoolSize),
        fixedPrizeAmount: fixedPrize,
        commissionBps:
          commissionPercent.trim() === '' ? null : Math.round(Number(commissionPercent) * 100),
        exitClosesBeforeSeconds:
          exitClosesBeforeSeconds.trim() === ''
            ? null
            : Number(exitClosesBeforeSeconds) * exitWindowUnitSeconds,
        joinClosesBeforeSeconds:
          joinClosesBeforeSeconds.trim() === '' ? null : Number(joinClosesBeforeSeconds) * 3600,
        dualWinnerChance: dualWinnerPct / 100,
        realPlayerGraceBalls: graceBalls,
        allowRealWinsOverPhantoms: allowRealWins,
      });
      onSaved(updated);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        {gameType.name}
        {!gameType.active && <span className="chip chip--muted"> paused</span>}
      </h2>

      {gameType.currentRound ? (
        <CurrentRoundStrip
          round={gameType.currentRound}
          manualStart={gameType.requiresManualStart}
        />
      ) : null}

      {/* The win-outcome knobs, deliberately set apart from the schedule and
          pricing fields: these decide how the game's money actually lands,
          so they get a section that reads as important instead of getting
          lost among the per-round settings below. */}
      <div className="card card--core">
        <h2>Win outcomes</h2>
        <p className="field__hint">
          How often a round ends with a real player's name on the payout —
          against a backdrop where phantoms hold most of the cards and would
          otherwise win most rounds by card share alone. Both changes apply
          only to rounds opened from now on; rounds already waiting or live
          are untouched.
        </p>

        {gameType.slug === 'normal' ? (
          <label className="field">
            <span className="field__label">
              <input
                type="checkbox"
                checked={allowRealWins}
                disabled={!editable}
                onChange={(e) => setAllowRealWins(e.target.checked)}
              />{' '}
              Campaign mode — let real players win however many phantoms are in the room
            </span>
            <span className="field__hint">
              Normally a real player can only win a round where real players outnumber the
              phantoms, because a real winner is paid out of the whole pool and phantom stakes
              are not money — so a phantom-heavy room pays them mostly out of house capital.
              Turn this on to run a campaign with the grace window above, which otherwise gets
              silently cancelled: the grace window promotes a real card and the rule then
              refuses it. <strong>Turn it off again afterwards.</strong> While it is on, every
              real win in this game type is funded by the house. Rounds already waiting or live
              keep the setting they opened with.
            </span>
          </label>
        ) : null}

        <label className="field">
          <span className="field__label">
            Real-player grace window — {graceBalls} ball{graceBalls === 1 ? '' : 's'}
          </span>
          <input
            className="input input--range"
            type="range"
            min={0}
            max={10}
            step={1}
            value={graceBalls}
            disabled={!editable}
            onChange={(e) => setGraceBalls(Number(e.target.value))}
          />
          <span className="field__hint">
            When a phantom card completes first, the round looks up to this
            many <em>already-predetermined</em> balls ahead — if a real
            player's card would win within them, it does. 0 = no preference
            (phantoms win roughly their share of the cards, ~70% of Normal
            rounds today). 3 is a typical strong setting. The draw order
            itself is never changed or re-rolled, so the round stays provably
            fair.
          </span>
        </label>

        <label className="field">
          <span className="field__label">Split-pot chance — {dualWinnerPct}%</span>
          <input
            className="input input--range"
            type="range"
            min={0}
            max={100}
            step={5}
            value={dualWinnerPct}
            disabled={!editable}
            onChange={(e) => setDualWinnerPct(Number(e.target.value))}
          />
          <span className="field__hint">
            Probability that a round is eligible to split the pot between two
            winners when two cards complete on the same draw (or within one
            ball of each other). Normal Bingo runs at 30% today; Arif Bingo
            at 0%.
          </span>
        </label>

        <WinCostPreview
          entryFee={entryFee}
          phantomMinPlayers={phantomMinPlayers}
          commissionPercent={commissionPercent}
          fixedPrizeAmount={fixedPrizeAmount}
        />
      </div>

      {gameType.cadence === 'interval' ? (
        <Row label="Cadence">{`A new round opens every ${gameType.intervalSeconds}s`}</Row>
      ) : (
        <Field
          label="Days"
          hint={`At ${gameType.scheduleTime} (${gameType.scheduleTimezone}). At least one day.`}
        >
          <div className="weekday-picker">
            {WEEKDAY_NAMES.map((name, day) => {
              const checked = scheduleDays.includes(day);
              return (
                <label key={day} className="weekday-picker__day">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!editable}
                    onChange={(e) =>
                      setScheduleDays(
                        e.target.checked
                          ? [...scheduleDays, day]
                          : scheduleDays.filter((d) => d !== day),
                      )
                    }
                  />
                  {name}
                </label>
              );
            })}
          </div>
        </Field>
      )}

      <Field label="Entry fee" hint="In ETB — 10 means ETB 10.00">
        <input
          className="input"
          inputMode="decimal"
          value={entryFee}
          disabled={!editable}
          onChange={(e) => setEntryFee(e.target.value)}
        />
      </Field>

      <Field label="Pattern">
        <select
          className="input"
          value={patternId}
          disabled={!editable}
          onChange={(e) => setPatternId(e.target.value)}
        >
          {patterns.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Minimum players" hint="Below this at the deadline, the round extends once, then cancels and refunds.">
        <input
          className="input"
          inputMode="numeric"
          value={minPlayers}
          disabled={!editable}
          onChange={(e) => setMinPlayers(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <Field label="Maximum players" hint="Cap on how many players a round may hold. Blank = no cap.">
        <input
          className="input"
          inputMode="numeric"
          value={maxPlayers}
          disabled={!editable}
          onChange={(e) => setMaxPlayers(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <Field
        label="Max cartelas per player"
        hint="How many cartelas a single player may hold in one round. Applies to the next round opened."
      >
        <input
          className="input"
          inputMode="numeric"
          value={maxCardsPerUser}
          disabled={!editable}
          onChange={(e) => setMaxCardsPerUser(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      {gameType.cadence === 'interval' && (
        <Field label="Seconds between rounds" hint="A new round opens this many seconds after the previous one.">
          <input
            className="input"
            inputMode="numeric"
            value={intervalSeconds}
            disabled={!editable}
            onChange={(e) => setIntervalSeconds(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      )}

      {gameType.cadence === 'interval' ? (
        <Field
          label="Concurrent rounds"
          hint="How many rounds stay open for players to join at once. 1 is the original behavior — raise it so someone who finds a round full has another already open."
        >
          <input
            className="input"
            inputMode="numeric"
            value={concurrentRounds}
            disabled={!editable}
            onChange={(e) => setConcurrentRounds(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      ) : (
        // Weekly (Arif): concurrency always equals however many days are
        // checked above — one round per day, never a separate setting an
        // operator could accidentally leave out of sync with the days.
        <Row label="Concurrent rounds">
          {scheduleDays.length} — one per checked day above
        </Row>
      )}

      <Field label="Seconds between draws" hint="Pace of the draw once a round is live. Shown in seconds; stored as milliseconds.">
        <input
          className="input"
          inputMode="numeric"
          value={drawIntervalMs}
          disabled={!editable}
          onChange={(e) => setDrawIntervalMs(e.target.value.replace(/[^\d.]/g, ''))}
        />
      </Field>

      <Field
        label="Minimum test players (phantom fill)"
        hint="⚠️ Only applies while phantom testing is enabled. Set to 0 to turn phantoms OFF for this game type — the only per-type switch there is, since the master toggle is global. Otherwise each round fills toward a RANDOM count between this number and the picker grid size above: it is a floor, not the count itself. Setting it equal to the grid size removes the randomness entirely (every round fills to exactly that many, which reads as scripted rather than organic). Leave blank to use the built-in default (150-200 for Normal Bingo)."
      >
        <input
          className="input"
          inputMode="numeric"
          value={phantomMinPlayers}
          disabled={!editable}
          onChange={(e) => setPhantomMinPlayers(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <Field
        label="Cartela picker grid size"
        hint="How many numbered slots the picker shows for this type. Blank = the code default (200)."
      >
        <input
          className="input"
          inputMode="numeric"
          value={cartelaPoolSize}
          disabled={!editable}
          onChange={(e) => setCartelaPoolSize(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <Field
        label="Fixed prize (flat jackpot)"
        hint="In ETB. When set, the winner gets exactly this amount regardless of pool size — everything else collected goes to house commission. Blank = the normal pool-percentage prize."
      >
        <input
          className="input"
          inputMode="decimal"
          value={fixedPrizeAmount}
          disabled={!editable}
          onChange={(e) => setFixedPrizeAmount(e.target.value)}
        />
      </Field>

      <Field
        label="House commission (%)"
        hint="Percentage of the pool the house keeps before the prize is paid. Blank = the standard tier (22%). Ignored by Arif — a flat-jackpot type pays the jackpot and the house keeps the pool surplus instead."
      >
        <input
          className="input"
          inputMode="decimal"
          value={commissionPercent}
          disabled={!editable}
          onChange={(e) => setCommissionPercent(e.target.value.replace(/[^\d.]/g, ''))}
        />
      </Field>

      <Field
        label={
          gameType.cadence === 'weekly'
            ? 'Exit window closes (hours before start)'
            : 'Exit window closes (seconds before start)'
        }
        hint="Overrides the universal leave-with-refund window (15 seconds before the scheduled start) with 'any time up until this long before the scheduled start'. Blank = the universal 15s rule. Arif ships with 10 hours."
      >
        <input
          className="input"
          inputMode="numeric"
          value={exitClosesBeforeSeconds}
          disabled={!editable}
          onChange={(e) => setExitClosesBeforeSeconds(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      {gameType.cadence === 'weekly' && (
        <Field
          label="Cartela buying closes (hours before start)"
          hint="Only applies to a weekly-scheduled type. Blank = buying stays open right up to the scheduled start."
        >
          <input
            className="input"
            inputMode="numeric"
            value={joinClosesBeforeSeconds}
            disabled={!editable}
            onChange={(e) => setJoinClosesBeforeSeconds(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      )}

      <label className="field field--checkbox">
        <input
          type="checkbox"
          checked={active}
          disabled={!editable}
          onChange={(e) => setActive(e.target.checked)}
        />
        <span>Active — unchecking pauses auto-opening new rounds for this type</span>
      </label>

      {editable && (
        <ConfirmButton
          confirm={`Save changes to ${gameType.name}? This only affects rounds opened from now on.`}
          onConfirm={() => void save()}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save'}
        </ConfirmButton>
      )}
    </div>
  );
}

/** Rough ETB display for an estimate, not a ledger amount — no santim rounding rules apply. */
function formatEtbApprox(n: number): string {
  const rounded = Math.round(n);
  const sign = rounded < 0 ? '−' : '';
  return `ETB ${sign}${Math.abs(rounded).toLocaleString('en-US')}`;
}

/**
 * A live estimate of what a single unassisted real win costs the house right
 * now, computed straight from the form's own fields — no round-trip to the
 * server, so it updates as an admin drags a slider, before they ever hit
 * Save. This is the number the reward-mode design (migration 031) is built
 * around: `settle()` pays a real winner a share of the *whole* pool, phantom
 * stakes included, so a lone real player against a heavily phantom-padded
 * room can cost far more real cash than that player staked.
 *
 * Deliberately pessimistic — one real player alone against the *ambient*
 * phantom minimum, the worst case this screen's numbers can produce on their
 * own. The actual reward-mode ratio cap (rewardPolicy.ts) keeps a real
 * *scheduled* win far cheaper than this by capping phantom count to the real
 * headcount at the moment of a win; this preview doesn't know about that
 * live ratio, only about the static numbers on this form.
 */
function WinCostPreview({
  entryFee,
  phantomMinPlayers,
  commissionPercent,
  fixedPrizeAmount,
}: {
  entryFee: string;
  phantomMinPlayers: string;
  commissionPercent: string;
  fixedPrizeAmount: string;
}) {
  const estimate = useMemo(() => {
    const fee = Number(entryFee.replace(/,/g, ''));
    if (!Number.isFinite(fee) || fee <= 0) return null;

    const fixedPrize =
      fixedPrizeAmount.trim() === '' ? null : Number(fixedPrizeAmount.replace(/,/g, ''));
    if (fixedPrize !== null && Number.isFinite(fixedPrize)) {
      // A flat jackpot doesn't scale down with phantom count — one lone real
      // winner costs the house the full jackpot minus what they staked,
      // regardless of how the room was filled.
      return { cost: fixedPrize - fee, basis: 'the flat jackpot' as const };
    }

    const phantoms = phantomMinPlayers.trim() === '' ? 0 : Number(phantomMinPlayers);
    if (!Number.isFinite(phantoms) || phantoms < 0) return null;
    const commissionBps =
      commissionPercent.trim() === '' ? 2200 : Math.round(Number(commissionPercent) * 100);

    const pool = (1 + phantoms) * fee;
    const netPrize = pool * (1 - commissionBps / 10000);
    return { cost: netPrize - fee, basis: `${phantoms} ambient phantoms` as const };
  }, [entryFee, phantomMinPlayers, commissionPercent, fixedPrizeAmount]);

  if (estimate === null) return null;

  return (
    <p className="field__hint">
      <strong>Estimated cost of one unassisted real win</strong> — a lone real
      player against {estimate.basis}: <strong>{formatEtbApprox(estimate.cost)}</strong>{' '}
      {estimate.cost > 0
        ? 'in real house cash beyond what that one player staked.'
        : 'i.e. no house cash beyond forgone commission — a cheap win at these settings.'}
    </p>
  );
}

/**
 * The round for this type that is currently open or running. Read-only: Arif
 * Bingo's Start handle lived here until 2026-08-26, when the runner took the
 * job over and starts each round at its own scheduled slot. A type with no open
 * round shows nothing at all.
 *
 * The endpoint behind that handle still exists, on the round detail screen —
 * `Rounds` → a round → Start — as the break-glass control for starting early or
 * recovering a slot the runner was down for. It is deliberately not here: this
 * screen is where an operator comes to read configuration, and a start button
 * on it invited a manual start as the normal path when it no longer is.
 */
function CurrentRoundStrip({
  round,
  manualStart,
}: {
  round: RoundView;
  manualStart: boolean;
}) {
  const shortPlayers = (round.playerCount ?? 0) < round.minPlayers;

  return (
    <div className="card card--compact">
      <Row label="Current round">
        <span className={`status status--${round.status}`}>{round.status}</span>{' '}
        <span className="muted">
          {round.playerCount ?? '—'} player{round.playerCount === 1 ? '' : 's'} ·{' '}
          {round.cardCount ?? '—'} cartelas
        </span>
      </Row>

      {round.status === 'waiting' ? (
        <p className="muted">
          {!manualStart
            ? 'Starts automatically at the join deadline — no action needed.'
            : shortPlayers
              ? `Will not start — needs ${round.minPlayers} players, has ${round.playerCount ?? 0}. A round still short at its scheduled time stays open rather than starting.`
              : `Starts automatically at ${at(round.startsAt)}: the 3-minute countdown runs from there, and the first number is drawn at the end of it. No action needed.`}
        </p>
      ) : null}
    </div>
  );
}
